// frontend/src/components/shared/CaseCallModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Phone, PhoneCall, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { logActivity } from '../../api';
import { useApp } from '../../context/AppContext';

export const CaseCallModal = ({ open, onOpenChange, caseData }) => {
  const { currentUserId, currentUser } = useApp();
  const [callStatus, setCallStatus] = useState('Connected - Positive');
  const [remarks, setRemarks] = useState('');
  const [duration, setDuration] = useState('2 mins');

  if (!caseData) return null;

  const phone = caseData.applicantPhone || '';
  const cleanPhone = phone.replace(/[^0-9+]/g, '');
  const userName = currentUser?.name || 'User';

  const handleLogCall = () => {
    toast.success(`Call log recorded for ${caseData.refNumber}!`);

    logActivity({
      action: 'Call Logged',
      description: `${userName} called ${caseData.applicantName} (${phone}) - Status: ${callStatus}. Notes: ${remarks || 'No remarks'}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200 shadow-xl rounded-xl">
        <DialogHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-600">
              <PhoneCall className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-slate-900">
                Call Applicant & Log Details
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Direct dial and log communication history for Case {caseData.refNumber}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Dial Card */}
          <div className="bg-teal-50/70 border border-teal-200 rounded-lg p-3.5 flex items-center justify-between">
            <div>
              <div className="text-xs text-teal-800 font-semibold">{caseData.applicantName}</div>
              <div className="font-mono text-sm font-bold text-teal-950 mt-0.5">{phone || 'No Phone Registered'}</div>
            </div>
            {cleanPhone ? (
              <a
                href={`tel:${cleanPhone}`}
                onClick={() => {
                  logActivity({
                    action: 'Direct Call Initiated',
                    description: `${userName} initiated call to ${caseData.applicantName} (${cleanPhone})`,
                    case_id: caseData.id,
                    user_id: currentUserId,
                  });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-xs font-bold transition-all shadow-xs"
              >
                <Phone className="w-3.5 h-3.5" />
                Call Now
              </a>
            ) : null}
          </div>

          {/* Outcome Status */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Call Outcome / Status</Label>
            <select
              value={callStatus}
              onChange={(e) => setCallStatus(e.target.value)}
              className="w-full text-xs h-9 rounded-md border border-slate-200 bg-white px-3 py-1 text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="Connected - Positive">Connected - Positive Verification</option>
              <option value="Connected - Appointment Scheduled">Connected - Appointment Scheduled</option>
              <option value="Connected - Revisit Requested">Connected - Revisit Requested</option>
              <option value="Ringing / No Answer">Ringing / No Answer</option>
              <option value="Switched Off / Not Reachable">Switched Off / Not Reachable</option>
              <option value="Wrong / Invalid Number">Wrong / Invalid Number</option>
            </select>
          </div>

          {/* Duration */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              Call Duration
            </Label>
            <Input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g. 3 mins"
              className="text-xs border-slate-200"
            />
          </div>

          {/* Remarks */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">Call Remarks / Discussion Notes</Label>
            <Textarea
              rows={3}
              placeholder="e.g. Applicant confirmed address and will be available tomorrow at 11 AM..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="text-xs border-slate-200 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleLogCall}
            className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5 text-xs font-semibold"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Save Call Log & Activity
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
